import React, { useEffect, useMemo, useState } from "react";

import { Loader2 } from "lucide-react";

import CardPreview from "./CardPreview";
import { errorText } from "./format";
import { sampleFields, toUserGuide } from "./guides";
import { draftTargets } from "./targets";
import {
  draftParameterValues,
  draftTier,
  type DraftPayload,
  effectiveSurface,
  type SignalAdminChange,
  type SignalAdminConfigResponse,
  type SignalAdminDraft,
  type SignalAdminOverview,
  type SignalAdminRegistry,
  valueLabel,
} from "./types";
import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";
import { SignalGuideContent } from "../../Pages/MyPortfolioAccounts/SignalGuideModal";
import { useAppDispatch } from "../../Store/Hooks";
import { previewSignalAdminPlatformConfig } from "../../Store/Reducers/SignalAdminSlice";

interface ApplyChangesModalProps {
  isOpen: boolean;
  onClose: () => void;
  current: SignalAdminConfigResponse;
  draft: SignalAdminDraft;
  payload: DraftPayload;
  overview: SignalAdminOverview | null;
  /** For previewing guide changes as users will see them. */
  registry: SignalAdminRegistry | null;
  applying: boolean;
  error: string;
  onApply: (reason: string) => void;
}

const ApplyChangesModal: React.FC<ApplyChangesModalProps> = ({
  isOpen,
  onClose,
  current,
  draft,
  payload,
  overview,
  registry,
  applying,
  error,
  onApply,
}) => {
  const dispatch = useAppDispatch();
  const [reason, setReason] = useState("");
  const [previewing, setPreviewing] = useState<string | null>(null);
  const targets = useMemo(
    () =>
      registry
        ? new Map(draftTargets(registry, draft).map(t => [t.id, t]))
        : new Map(),
    [registry, draft]
  );
  const [changes, setChanges] = useState<SignalAdminChange[] | null>(null);
  const [previewError, setPreviewError] = useState("");

  // The reason is cleared only when the modal closes (Cancel, or a
  // successful apply closes it) — not when the payload is rebuilt, e.g. by
  // the policy reload that picking an organization starts (S-65).
  useEffect(() => {
    if (!isOpen) setReason("");
  }, [isOpen]);

  // The server validates the draft exactly as an apply would and returns
  // the labelled change list — so what's shown here is what gets recorded.
  useEffect(() => {
    if (!isOpen) return undefined;
    setChanges(null);
    setPreviewError("");
    let cancelled = false;
    (async () => {
      try {
        const res = await dispatch(
          previewSignalAdminPlatformConfig(
            payload as unknown as Record<string, unknown>
          )
        ).unwrap();
        if (!cancelled) setChanges(res ?? []);
      } catch (e) {
        if (!cancelled)
          setPreviewError(errorText(e, "Couldn't validate this draft."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, isOpen, payload]);

  const wasVisible = current.presentation.signals_visible;
  const willBeVisible = effectiveSurface(draft) === "enabled";

  let impact: string | null = null;
  if (overview && wasVisible !== willBeVisible) {
    const counts = `${overview.open_signals} open signal${overview.open_signals === 1 ? "" : "s"} on ${overview.accounts_with_open_signals} account${overview.accounts_with_open_signals === 1 ? "" : "s"}`;
    const users = `${overview.users_with_open_signals} user${overview.users_with_open_signals === 1 ? "" : "s"}`;
    impact = willBeVisible
      ? `${counts} become visible again to ${users}, along with anything generated while signals were hidden.`
      : `${counts} will be hidden from ${users}. Signals keep being evaluated; nothing is withdrawn or deleted.`;
  }
  const touchesRules = Boolean(payload.layers || payload.rules);
  const touchesParams = Boolean(payload.parameters);

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={onClose}
      title="Review and apply"
      subtitle={
        <span className="text-[13px] font-normal text-gray-500">
          All organizations · becomes version {current.version + 1}
        </span>
      }
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button variant="white" onClick={onClose} disabled={applying}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => onApply(reason.trim())}
            disabled={!reason.trim() || !changes?.length || !!previewError}
            loading={applying}
          >
            Apply now
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {changes === null && !previewError && (
          <div className="flex items-center gap-2 text-[12.5px] text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Validating changes…
          </div>
        )}
        {previewError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700">
            {previewError}
          </div>
        )}
        {changes && changes.length > 0 && (
          <ul className="flex max-h-[420px] flex-col gap-1.5 overflow-y-auto">
            {changes.map(c => {
              // Guide changes can be checked exactly as a card will show them.
              const guideId = c.field.startsWith("guides.")
                ? c.field.slice("guides.".length)
                : null;
              const copyId = c.field.startsWith("card_copy.")
                ? c.field.slice("card_copy.".length)
                : null;
              const guide = guideId ? draft.guides[guideId] : undefined;
              const definition = registry?.definitions.find(
                d => d.id === (guideId ?? copyId)
              );
              return (
                <li
                  key={c.field}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-[12.5px]"
                >
                  {/* Wraps: a long value drops below the label instead of
                      squeezing it into a one-word column. */}
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                    <span className="min-w-0 font-semibold text-gray-700">
                      {c.label}
                    </span>
                    <span className="min-w-0 break-words">
                      <span className="text-gray-400">
                        {valueLabel(c.from)}
                      </span>
                      <span className="mx-2 text-gray-300">→</span>
                      <span className="font-bold text-gray-900">
                        {valueLabel(c.to)}
                      </span>
                    </span>
                  </div>
                  {copyId && definition && (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          setPreviewing(p => (p === c.field ? null : c.field))
                        }
                        className="mt-1 text-[11.5px] font-semibold text-brand hover:underline"
                      >
                        {previewing === c.field
                          ? "Hide preview"
                          : "Preview as users see it"}
                      </button>
                      {previewing === c.field && (
                        <div className="mt-2">
                          <CardPreview
                            definition={definition}
                            label={
                              draft.ctas[definition.id]?.label ??
                              definition.cta_effective.label
                            }
                            copy={draft.card_copy[definition.id]}
                            parameters={draftParameterValues(registry, draft)}
                            tier={draftTier(definition, draft)}
                          />
                        </div>
                      )}
                    </>
                  )}
                  {guide && definition && (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          setPreviewing(p => (p === c.field ? null : c.field))
                        }
                        className="mt-1 text-[11.5px] font-semibold text-brand hover:underline"
                      >
                        {previewing === c.field
                          ? "Hide preview"
                          : "Preview as users see it"}
                      </button>
                      {previewing === c.field && (
                        <div className="mt-2 overflow-hidden rounded-xl border border-gray-200">
                          <SignalGuideContent
                            preview
                            guide={toUserGuide(guide, targets)}
                            accountName="Example account"
                            signalTitle={definition.title}
                            tier={definition.tier}
                            fields={{
                              ...draftParameterValues(registry, draft),
                              ...sampleFields(definition.id),
                            }}
                            primaryLabel={
                              draft.ctas[definition.id]?.label ??
                              definition.cta_effective.label
                            }
                          />
                        </div>
                      )}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {impact && (
          <div
            className={`rounded-lg px-3 py-2.5 text-[12.5px] leading-relaxed ${
              willBeVisible
                ? "bg-emerald-50 text-emerald-900"
                : "bg-amber-50 text-amber-900"
            }`}
          >
            {impact}
          </div>
        )}
        {(touchesRules || touchesParams) && (
          <div className="rounded-lg bg-gray-50 px-3 py-2.5 text-[12px] leading-relaxed text-gray-600">
            {touchesRules &&
              "Signals that stop running are hidden from users; their open signals are left as they are, not resolved. "}
            Applying re-checks every account, so cards update within a few
            minutes.
          </div>
        )}

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-gray-700">
            Reason <span className="text-red-600">*</span>
          </span>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            placeholder="Why is this change being made? This is recorded in Change Audit."
            className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-[13px] text-gray-900 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          />
        </label>

        <p className="text-xs text-gray-500">
          Applies immediately for every organization and is recorded in Change
          Audit.
        </p>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700">
            {error}
          </div>
        )}
      </div>
    </CommonModal>
  );
};

export default ApplyChangesModal;
